import { getApiUrl } from "../api";
import { useEffect, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { getPolicyDetailsPath } from "./FoundationPolicyDetails";
import { ArabicHeader } from "./ArabicHome";

const getJwtToken = () => {
  const keys = ["token", "accessToken", "jwt", "authToken"];
  for (const key of keys) {
    const token = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (token?.trim()) return token.replace(/^Bearer\s+/i, "").trim();
  }

  return (process.env.REACT_APP_JWT_TOKEN || "").replace(/^Bearer\s+/i, "").trim();
};


const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const mapPolicy = (item, index, language = "en") => {

  return {
    id: item?.id || String(index + 1),
    title: stripHtml(language === "ar" ? item?.title_ar || item?.title || item?.title_en : item?.title_en || item?.title),
    subtitle_en: language === "ar" ? item?.subtitle_ar || item?.subtitle || item?.subtitle_en : item?.subtitle_en,
    icon: item?.icon || item?.ImagePath || "",
  };
};

export default function FoundationPolicies({ language = "en" }) {
  const interactions = useHomeInteractions();
  const [policies, setPolicies] = useState([]);

  useEffect(() => {
    document.title = "Foundation's Policies | Tarahum Charity Foundation";
  }, [language]);

  useEffect(() => {
    if (language !== "ar") return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => { document.body.classList.remove("arabic-layout-page"); document.documentElement.lang = "en"; document.documentElement.dir = "ltr"; };
  }, [language]);

  useEffect(() => {
    const controller = new AbortController();

    const loadPolicies = async () => {
      try {
        const headers = { Accept: "application/json" };
        const token = getJwtToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(getApiUrl("/api/GetFoundationPoliciesList.ashx?page=1&pageSize=200"), {
          headers,
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Foundation policies request failed with ${response.status}`);
        }

        const result = await response.json();
        const rows = Array.isArray(result?.data) ? result.data : [];
        const mappedRows = rows.map((item, index) => mapPolicy(item, index, language)).filter((item) => item.title);

        if (mappedRows.length) {
          setPolicies(mappedRows);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("Unable to load foundation policies", error);
        }
      }
    };

    loadPolicies();

    return () => controller.abort();
  }, [language]);

  return (
    <>
      <Preloader />
      {language === "ar" ? <ArabicHeader /> : <Header />}
      {language === "ar" ? null : <LanguagePopup />}

      <section className="policy-list--wrt pb-0">
        <div className="policy-container">
          <div className="policy-hero">
            <h1>{language === "ar" ? "سياسات المؤسسة" : "Foundation's Policies"}</h1>
            <ul className="policy-breadcrumb">
              <li>
                <Link to="/">{language === "ar" ? "الرئيسية" : "home"}</Link>
              </li>
              <li>/</li>
              <li>{language === "ar" ? "سياسات المؤسسة" : "Foundation's Policies"}</li>
            </ul>
          </div>
        </div>

        <div className="policy-layout">
          <div className="container">
            <main className="policy-main">
              <div className="abt-brief-content">
                <h3>{language === "ar" ? "السياسات ووثائق الحوكمة" : "Policies and Governance Documents"}</h3>
                <p>
                  {language === "ar"
                    ? "استعرض السياسات الرسمية لمؤسسة تراحم، والإرشادات، والشروط والأحكام، ومعلومات الخصوصية، ومعايير الحوكمة، وممارسات الجودة، وإجراءات الإبلاغ."
                    : "Explore Tarahum Foundation's official policies, guidelines, terms, privacy information, governance standards, quality practices, and reporting procedures."}
                </p>
              </div>

              <div className="policy-grid">
                {policies.map((policy, index) => (
                  <div className="policy-card" id={`policy-${String(index + 1).padStart(2, "0")}`} key={policy.id || policy.title}>
                    <div className="policy-card-top">
                      <div className="policy-icon">
                        <i className={policy.icon}></i>
                      </div>
                      <div className="policy-number">{String(index + 1).padStart(2, "0")}</div>
                    </div>
                    <h3>{policy.title}</h3>
                    {policy.subtitle_en ? <p>{policy.subtitle_en}</p> : null}
                    <Link to={getPolicyDetailsPath(policy)} className="policy-btn">
                      View Policy <i className="fa-solid fa-arrow-right"></i>
                    </Link>
                  </div>
                ))}
              </div>
            </main>
          </div>
        </div>
      </section>

      <Footer language={language} />
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
